import { LocationPage, GenericPage, SectionWrapper, GenericWrapper } from "@orlandohealth/build-kit";
import type { BasePageProps } from "@orlandohealth/build-kit";
import { useRouter } from "next/router";

const LocationPageComponent = LocationPage.createPage(SectionWrapper, "location");
const GenericPageComponent = GenericPage.createPage(GenericWrapper, "page");

/**
 * Facilities route: top-level (/facilities/:slug) uses Location model or Page with locationId.
 * Nested (/facilities/:slug/...) uses Page model only.
 */
function FacilitiesPage(props: BasePageProps & { isNestedFacilityPage?: boolean }) {
  const { isNestedFacilityPage, ...rest } = props;
  const router = useRouter();
  if (isNestedFacilityPage) {
    return <GenericPageComponent key={router.asPath} {...rest} />;
  }
  return <LocationPageComponent key={router.asPath} {...rest} />;
}

export default FacilitiesPage;

export const getStaticPaths = async (_context: any) => {
  if (process.env.SKIP_SSG === "true") {
    return { paths: [], fallback: "blocking" };
  }
  const { generateFacilitiesPaths } = await import("@orlandohealth/build-kit");
  const paths = await generateFacilitiesPaths(["en"]);

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps = async (context: any) => {
  const { params, locale, defaultLocale, preview = false, previewData = {} } = context;
  const slugParts = Array.isArray(params?.slug) ? params?.slug : params?.slug ? [params.slug] : [];
  const slug = slugParts.join("/");

  if (!slug) {
    return { notFound: true, revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10) };
  }

  const urlPath = `/facilities/${slug}`;
  const isTopLevel = slugParts.length === 1;
  const isInBuilder =
    !!(previewData as any)?.["builder.space"] || !!(previewData as any)?.["builder.frameEditing"];

  if (isTopLevel) {
    return LocationPage.getStaticProps(
      urlPath,
      slug,
      locale,
      defaultLocale,
      "location"
    );
  }

  const result = await GenericPage.getStaticProps(
    urlPath,
    locale,
    defaultLocale,
    "page",
    preview || isInBuilder
  );

  if (!result.props.content && !preview && !isInBuilder) {
    return { notFound: true, revalidate: parseInt(process.env.ISR_REVALIDATE_SECONDS || "300", 10) };
  }

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "";
  const parentFacilityUrl = `${siteUrl}/facilities/${slugParts[0]}`;
  const facilityEntity = {
    "@type": "MedicalOrganization",
    "@id": `${parentFacilityUrl}#location`,
    url: parentFacilityUrl,
  };

  const enrichment = result.props?.schemaEnrichment;

  return {
    ...result,
    props: {
      ...result.props,
      locale,
      defaultLocale,
      isNestedFacilityPage: true,
      schemaEnrichment: {
        subOrganizations: enrichment?.subOrganizations || [],
        additionalEntities: [
          ...(enrichment?.additionalEntities || []),
          facilityEntity,
        ],
      },
    },
  };
}; 